import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getLifecycleLabel, getLifecycleStageLabel, getMiningMethodLabel } from "./resourceUtils";
import { formatProductionUnit } from "./productionUtils";
import { formatLastUpdated, getStatusChipProps } from "./statusUtils";
import { ResourceTable } from "./ResourceTables";
import {
  formatCurrencyValue,
  formatDecimal,
  formatPriceSummary,
} from "../../utils/resourceFormatting";
import { formatNumberWithCommas } from "../../utils/companiesFormatting";

export default function ResourceSection({
  assetId,
  reserves,
  resourceReportsByReserve,
  productionReportsByReserve,
  isMobile,
  canEdit,
  onAddResource,
  onEditResourceReserve,
  onDuplicateResourceReserve,
  onRequestDeleteResourceReserve,
  onAddReport,
  onEditReport,
  onDuplicateReport,
  onRequestDeleteReport,
  onAddProductionReport,
  onEditProductionReport,
  onDuplicateProductionReport,
  onRequestDeleteProductionReport,
}) {
  const getTaxLabel = (value) => {
    if (value === "pre_tax") return "Pre-tax";
    if (value === "after_tax") return "After-tax";
    return "N/A";
  };
  const getLatestDate = (dates = []) => {
    const cleaned = dates.filter(Boolean);
    if (!cleaned.length) return null;
    return cleaned.sort().slice(-1)[0];
  };
  const renderActualMetricsTable = (report) => {
    const values = report.values || [];
    if (!values.length) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          No KPI values yet.
        </Typography>
      );
    }
    const entries = values.map((val) => {
      const commodityName = val.commodity?.name;
      const metricName = val.metric?.name || "Metric";
      const label = commodityName ? `${commodityName} - ${metricName}` : metricName;
      return {
        key: val.id || `${metricName}-${commodityName || "na"}`,
        label,
        value: formatNumberWithCommas(val.value),
        unit: formatProductionUnit(val.metric?.unit, report.reporting_currency),
      };
    });

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
          mt: 1,
        }}
      >
        {entries.map((entry) => (
          <Box
            key={entry.key}
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1,
              px: 1.25,
              py: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {entry.label}
            </Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {entry.value} {entry.unit}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };
  return (
    <Box>
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={1}
        alignItems={isMobile ? "flex-start" : "center"}
        justifyContent="space-between"
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Resources & Reserves
        </Typography>
        <Button
          variant="contained"
          onClick={() => onAddResource(assetId)}
          disabled={!canEdit}
        >
          Add Resources & Reserves
        </Button>
      </Stack>
      {reserves.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No resources & reserves yet.
        </Typography>
      ) : (
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {reserves.map((reserve) => {
            const reserveDeleteRequested = reserve.status === "delete_requested";
            const reports = resourceReportsByReserve[reserve.id] || [];
            const productionReports = (productionReportsByReserve?.[reserve.id] || []).filter(
              (report) => String(report.kind || "").toLowerCase() !== "forecast"
            );
            const latestResourceDate = getLatestDate(
              reports.map((report) => report.period?.as_of_date)
            );
            const latestProductionDate = getLatestDate(
              productionReports.map(
                (report) => report.period?.as_of_date || report.as_of_date
              )
            );
            return (
              <Accordion
                key={reserve.id}
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1.25,
                  boxShadow: "none",
                  backgroundColor: reserveDeleteRequested
                    ? "rgba(15, 23, 42, 0.06)"
                    : "background.paper",
                  opacity: reserveDeleteRequested ? 0.62 : 1,
                  "&::before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  component="div"
                  sx={{
                    px: 1.5,
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      mr: 1,
                    },
                  }}
                >
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={isMobile ? "flex-start" : "center"}
                    sx={{ width: "100%" }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {reserve.name || "Resources & Reserves"}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          {...getStatusChipProps(reserve.status)}
                        />
                        {reserveDeleteRequested && (
                          <Typography variant="caption" color="error.main" fontWeight={700}>
                            Pending deletion in next submission
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Last updated: {formatLastUpdated(reserve.last_updated_at)}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {`Mining method: ${getMiningMethodLabel(
                          reserve.mining_method
                        )} - Lifecycle: ${getLifecycleLabel(
                          reserve.lifecycle
                        )} - Lifecycle Stage: ${getLifecycleStageLabel(
                          reserve.lifecycle_stage
                        )}`}
                      </Typography>
                      <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary">
                          {`Resource estimates: ${reports.length} | Latest: ${
                            latestResourceDate || "N/A"
                          }`}
                          {` - `}
                          {`Production reports: ${productionReports.length} | Latest: ${
                            latestProductionDate || "N/A"
                          }`}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditResourceReserve?.(reserve);
                        }}
                        disabled={!canEdit || reserveDeleteRequested}
                        sx={{ minWidth: "auto", px: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </Button>
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDuplicateResourceReserve?.(reserve);
                        }}
                        disabled={!canEdit || reserveDeleteRequested}
                        sx={{ minWidth: "auto", px: 0 }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRequestDeleteResourceReserve?.({
                            reserveId: reserve.id,
                            assetId: reserve.asset?.id || reserve.asset_id || reserve.asset,
                            label: reserve.name || "Resources & Reserves",
                          });
                        }}
                        disabled={!canEdit || reserveDeleteRequested}
                        sx={{ minWidth: "auto", px: 1.25 }}
                      >
                        <CloseIcon fontSize="small" />
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5 }}>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={isMobile ? "flex-start" : "center"}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Resource Estimate Reports
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => onAddReport(reserve.id)}
                      disabled={!canEdit}
                    >
                      Add Resource Estimate
                    </Button>
                  </Stack>
                  {reports.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No resource estimates yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {reports.map((report) => {
                        const reportDeleteRequested = report.status === "delete_requested";
                        return (
                        <Accordion
                          key={report.id}
                          sx={{
                            border: "1px dashed rgba(0,0,0,0.15)",
                          borderRadius: 0.75,
                          boxShadow: "none",
                          backgroundColor: reportDeleteRequested
                            ? "rgba(15, 23, 42, 0.06)"
                            : "background.paper",
                          opacity: reportDeleteRequested ? 0.62 : 1,
                          "&::before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          component="div"
                          sx={{
                            px: 1.5,
                            "& .MuiAccordionSummary-expandIconWrapper": {
                              mr: 1,
                            },
                          }}
                        >
                          <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={isMobile ? "flex-start" : "center"}
                            sx={{ width: "100%" }}
                          >
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
                                >
                                  Report Snapshot
                                </Typography>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  {...getStatusChipProps(report.status)}
                                />
                                {reportDeleteRequested && (
                                  <Typography variant="caption" color="error.main" fontWeight={700}>
                                    Pending deletion in next submission
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  Last updated: {formatLastUpdated(report.last_updated_at)}
                                </Typography>
                              </Stack>
                              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                <Typography variant="body2">
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    As-of:
                                  </Box>{" "}
                                  {report.period?.as_of_date || "Not set"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Source:
                                  </Box>{" "}
                                  {report.source_title || "Not set"}
                                </Typography>
                                {report.source_url && (
                                  <Typography variant="caption" color="text.secondary">
                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                      URL:
                                    </Box>{" "}
                                    {report.source_url}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onEditReport(report, reserve.id);
                                }}
                                disabled={!canEdit || reserveDeleteRequested || reportDeleteRequested}
                                sx={{ minWidth: "auto", px: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </Button>
                              <Button
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onDuplicateReport?.(report, reserve.id);
                                }}
                                disabled={!canEdit || reserveDeleteRequested || reportDeleteRequested}
                                sx={{ minWidth: "auto", px: 0 }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onRequestDeleteReport({
                                    reportId: report.id,
                                    reserveId: reserve.id,
                                    asOfDate: report.period?.as_of_date,
                                  });
                                }}
                                disabled={!canEdit || reserveDeleteRequested || reportDeleteRequested}
                                sx={{ minWidth: "auto", px: 1.25 }}
                              >
                                <CloseIcon fontSize="small" />
                              </Button>
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 0.5, sm: 1.5 } }}>
                          <Box
                            sx={{
                              mt: 1,
                              overflowX: { xs: "scroll", md: "hidden" },
                              WebkitOverflowScrolling: "touch",
                            }}
                          >
                            <Box sx={{ minWidth: { xs: 560, sm: "auto" } }}>
                              <ResourceTable values={report.values || []} />
                            </Box>
                          </Box>
                          <Divider sx={{ my: 1.5 }} />
                          <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={1}
                            alignItems={isMobile ? "flex-start" : "center"}
                            justifyContent="space-between"
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              Economic Scenarios (NPV)
                            </Typography>
                          </Stack>
                          {(!report.economic_scenarios ||
                            report.economic_scenarios.length === 0) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              No economic scenarios yet.
                            </Typography>
                          )}
                          {report.economic_scenarios?.length > 0 && (
                            <Box
                              sx={{
                                border: "1px solid rgba(0,0,0,0.08)",
                                borderRadius: 1.5,
                                overflowX: { xs: "scroll", md: "hidden" },
                                WebkitOverflowScrolling: "touch",
                                mt: 1,
                              }}
                            >
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "minmax(140px, 1.2fr) minmax(90px, 0.8fr) minmax(90px, 0.8fr) minmax(90px, 0.8fr) minmax(180px, 1.4fr) minmax(120px, 1fr) minmax(90px, 0.8fr) minmax(170px, 0.9fr)",
                                    gap: { xs: 0.5, sm: 1 },
                                    px: { xs: 0.5, sm: 1 },
                                    py: { xs: 0.5, sm: 0.75 },
                                    backgroundColor: "rgba(0,0,0,0.03)",
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={700}>
                                    Name
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700}>
                                    Currency
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700}>
                                    Tax
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700}>
                                    Discount
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700}>
                                    Price Assumptions
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700}>
                                    NPV
                                  </Typography>
                                <Typography variant="caption" fontWeight={700}>
                                  IRR
                                </Typography>
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{ whiteSpace: "normal", lineHeight: 1.2 }}
                                >
                                  Payback<br />Period
                                </Typography>
                              </Box>
                              <Stack spacing={0} sx={{ px: { xs: 0.5, sm: 1 }, py: { xs: 0.5, sm: 0.75 } }}>
                                {report.economic_scenarios.map((scenario) => (
                                    <Box
                                      key={scenario.id}
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "minmax(140px, 1.2fr) minmax(90px, 0.8fr) minmax(90px, 0.8fr) minmax(90px, 0.8fr) minmax(180px, 1.4fr) minmax(120px, 1fr) minmax(90px, 0.8fr) minmax(100px, 0.9fr)",
                                        gap: { xs: 0.5, sm: 1 },
                                        alignItems: "center",
                                        py: 0.5,
                                      }}
                                    >
                                      <Typography variant="caption" fontWeight={600}>
                                        {scenario.label || "Scenario"}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {scenario.currency || "USD"}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {getTaxLabel(scenario.tax_treatment)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {scenario.discount_rate !== null &&
                                        scenario.discount_rate !== undefined
                                          ? `${formatDecimal(scenario.discount_rate, 2)}%`
                                          : "N/A"}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatPriceSummary(scenario)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatCurrencyValue(
                                          scenario.npv_value,
                                          scenario.currency,
                                          0
                                        )}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {scenario.irr_value !== null &&
                                        scenario.irr_value !== undefined
                                          ? `${formatDecimal(scenario.irr_value, 2)}%`
                                          : "N/A"}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {scenario.payback_period !== null &&
                                        scenario.payback_period !== undefined
                                          ? `${formatDecimal(scenario.payback_period, 2)} yrs`
                                          : "N/A"}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Stack>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                        );
                      })}
                  </Stack>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={isMobile ? "flex-start" : "center"}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Production Reports
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => onAddProductionReport?.(reserve.id)}
                      disabled={!canEdit}
                    >
                      Add Production Report
                    </Button>
                  </Stack>
                  {productionReports.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No production reports yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {productionReports.map((report) => {
                        const productionReportDeleteRequested =
                          report.status === "delete_requested";
                        return (
                        <Accordion
                          key={report.id}
                          sx={{
                            border: "1px dashed rgba(0,0,0,0.15)",
                            borderRadius: 0.75,
                            boxShadow: "none",
                            opacity: productionReportDeleteRequested ? 0.62 : 1,
                            "&::before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            component="div"
                            sx={{
                              px: 1.5,
                              "& .MuiAccordionSummary-expandIconWrapper": {
                                mr: 1,
                              },
                            }}
                          >
                            <Stack
                              direction={isMobile ? "column" : "row"}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={isMobile ? "flex-start" : "center"}
                              sx={{ width: "100%" }}
                            >
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
                                  >
                                    Production Snapshot
                                  </Typography>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    {...getStatusChipProps(report.status)}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Last updated: {formatLastUpdated(report.last_updated_at)}
                                  </Typography>
                                  {productionReportDeleteRequested && (
                                    <Typography variant="caption" color="error.main" fontWeight={700}>
                                      Pending deletion in next submission
                                    </Typography>
                                  )}
                                </Stack>
                                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                  <Typography variant="body2">
                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                      Period:
                                    </Box>{" "}
                                    {report.period?.label ||
                                      report.period?.as_of_date ||
                                      "Not set"}
                                  </Typography>
                                  {report.as_of_date && (
                                    <Typography variant="caption" color="text.secondary">
                                      <Box component="span" sx={{ fontWeight: 600 }}>
                                        As-of:
                                      </Box>{" "}
                                      {report.as_of_date}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                      Source:
                                    </Box>{" "}
                                    {report.source_title || "Not set"}
                                  </Typography>
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <Button
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onEditProductionReport?.(report, reserve.id);
                                  }}
                                  disabled={!canEdit || productionReportDeleteRequested}
                                  sx={{ minWidth: "auto", px: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </Button>
                                <Button
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onDuplicateProductionReport?.(report, reserve.id);
                                  }}
                                  disabled={!canEdit || productionReportDeleteRequested}
                                  sx={{ minWidth: "auto", px: 0 }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onRequestDeleteProductionReport?.({
                                      reportId: report.id,
                                      reserveId: reserve.id,
                                      label: report.period?.label || report.period?.as_of_date,
                                    });
                                  }}
                                  disabled={!canEdit || productionReportDeleteRequested}
                                  sx={{ minWidth: "auto", px: 1.25 }}
                                >
                                  <CloseIcon fontSize="small" />
                                </Button>
                              </Stack>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 1.5 }}>
                            {renderActualMetricsTable(report)}
                          </AccordionDetails>
                        </Accordion>
                        );
                      })}
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
